import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import DomPurify from "isomorphic-dompurify";
import { useTranslation } from "react-i18next";
import styles from "./fix-text.module.scss";
import font from "@/style/fonts.module.scss";
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import { Button } from "@headlessui/react";
import { Icon } from "@/elements-react/fix-icon";

export type FixTextProps = {
  type?: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;
  text: string;
  showMore?: boolean;
};

const COLLAPSE_HEIGHT = 100;

const FixText: React.FC<FixTextProps> = ({ type = LayoutStyleTypes.TYPE_LG, text, showMore = true }) => {
  const { t } = useTranslation();
  const textRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showMoreButton, setShowMoreButton] = useState(false);

  useEffect(() => {
    if (!showMore) {
      setCollapsed(false);
      setShowMoreButton(false);
      return;
    }
    const el = textRef.current;
    if (el) {
      // SSR-safe: only in effect
      setCollapsed(el.scrollHeight >= COLLAPSE_HEIGHT);
      setShowMoreButton(el.scrollHeight >= COLLAPSE_HEIGHT);
    }
  }, [text, showMore]);

  const handleShowMore = () => {
    setCollapsed(false);
    setShowMoreButton(false);
  };

  return (
    <div className={styles.root}>
      <div className={classNames(styles.container, type && styles[type], { [styles.collapsed]: collapsed })}>
        <div className={styles.text} ref={textRef} dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(text) }} />

        {showMoreButton && collapsed && (
          <>
            <div className={styles.overlay} />
            <Button className={classNames(styles.moreButton, styles[LayoutStyleTypes.TYPE_SM])} onClick={handleShowMore}>
              <span>{t("fu61huzb2j32x15f.show-more", "Show more")}</span>
              <Icon name="down" className={styles.caret} width={16} />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default FixText;
